import { GridColDef, GridCellParams } from '@mui/x-data-grid';
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridModel, DataGrid } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { Box, Button, Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import { LinkOff, Warning } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { useGlobals } from 'src/shared/contexts/appContext';
import { toast } from 'react-toastify';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { deleteMemberAutogiroStatus, getAllMemberAutogiroStatus } from 'src/shared/services/memberAutogiroStatus';
import { MemberAutogiroStatusResponse } from 'src/modules/member/models/memberAutogiroStatusResponse';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { displayAutogiroStatus } from 'src/shared/models/enums/autogiroPeriodicity.enum';
import { PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { MemberAutogiroStatus } from '../models/memberAutogiroStatus';
import { MandateStatusWarning, mandateBlocksPayment } from './mandateStatusWarning';

type MemberAutogiroStatusDataGridProps = {
  loadingCallback: (loading: boolean) => void;
  searchFilter: MemberAutogiroStatus;
  searchFilterCallback: (filter: MemberAutogiroStatus) => void;
};

export const MemberAutogiroStatusDataGrid = (props: MemberAutogiroStatusDataGridProps) => {
  const { loadingCallback, searchFilter, searchFilterCallback } = props;
  const { t } = useTranslation();
  const { currentUser, dateStamp, setDateStamp } = useGlobals();
  const { scrollIntoView, scrollRef } = useScrollIntoView();
  const isMounted = useIsMounted();

  // För framtida hantering [onRowClick]
  // const [modalOpen, setModalOpen] = useState<boolean>(false);
  // const [selectedItem, setSelectedItem] = useState<MemberAutogiroStatusResponse>();

  const [rows, setRows] = useState<MemberAutogiroStatusResponse[]>([]);
  const [rowToEnd, setRowToEnd] = useState<MemberAutogiroStatusResponse | undefined>(undefined);

  // Förvalt ikryssad: när medgivandet inte går att använda är det nästan alltid rätt att ta bort
  // det också, annars blir medlemmen kvar med ett medgivande som aldrig kan dras på.
  const [deleteMandate, setDeleteMandate] = useState<boolean>(true);

  const canDeleteMandate = getPermission(currentUser, PermissionEnum.AutogiroDelete);
  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageIndex,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
      loadingCallback(true);
    }

    try {
      const result = await getAllMemberAutogiroStatus(searchFilter);
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);

        setTimeout(() => {
          ConsoleHelper.log(dateStamp);
          setRows(
            result.data.results.map((item) => {
              return { ...item, id: item.autogiroStatus_id };
            }),
          );
        }, 100);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
        loadingCallback(false);
      }
    }
  }, [isMounted, loadingCallback, searchFilter, dateStamp]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  // const onRowClick = (params) => {
  //   setSelectedItem(params.row);
  //   setModalOpen(true);
  // };

  const isExpireDateAhead = (row: MemberAutogiroStatusResponse) => {
    if (!row.expireDate || !row.nextInvoiceAfter) return false;
    const expire = row.expireDate.replace(/-/g, '');
    const nextInvoice = row.nextInvoiceAfter.slice(0, 10).replace(/-/g, '');
    return expire > nextInvoice;
  };

  // Saknat inträdesdatum är normalt i sig, den som väntar på sin första dragning har varken
  // inträdes- eller förfallodatum och tas med i aviseringen. Det trasiga läget är kombinationen
  // utan inträdesdatum men med förfallodatum: då faller medlemmen mellan urvalets två grenar och
  // dras aldrig. Se sSQLExpired och sSQLPendingFirstPayment i workerns InvoiceService.
  const isMissingAdmDate = (row: MemberAutogiroStatusResponse) => !row.admDate && !!row.expireDate;

  // Medgivandet är makulerat hos Bankgirot men autogirot ligger kvar som betalsätt hos oss.
  // Borttagningen sker inte automatiskt, kunden ska hinna kontakta medlemmen eller teckna ett nytt
  // medgivande innan medlemmen flyttas över till vanlig avisering.
  // Knappen visas på alla rader där dragningen inte blir av, inte bara de makulerade. Valet är
  // detsamma i samtliga lägen: antingen rättar kunden medgivandet eller så tas autogiro bort som
  // betalmetod och medlemmen går till vanlig avisering.
  const needsAction = (row: MemberAutogiroStatusResponse) => mandateBlocksPayment(row.hasMandate, row.mandateStatus);

  const renderWarnings = (row: MemberAutogiroStatusResponse) => {
    const warnings: JSX.Element[] = [];

    if (isExpireDateAhead(row)) {
      warnings.push(
        <Tooltip key='expire' title={t('invoice:autogiro.expireDateAheadWarning')} arrow placement='left'>
          <Warning color='warning' sx={{ fontSize: 20 }} />
        </Tooltip>,
      );
    }

    if (isMissingAdmDate(row)) {
      warnings.push(
        <Tooltip key='admDate' title={t('invoice:autogiro.noAdmDateWarning')} arrow placement='left'>
          <Warning color='error' sx={{ fontSize: 20 }} />
        </Tooltip>,
      );
    }

    // Medgivandets status är gemensam med fliken Kopplade månadsgivare, se mandateStatusWarning.
    return (
      <>
        <MandateStatusWarning hasMandate={row.hasMandate} status={row.mandateStatus} />
        {warnings}
      </>
    );
  };

  const endAutogiro = async () => {
    if (!rowToEnd) return;

    try {
      await deleteMemberAutogiroStatus(rowToEnd.autogiroStatus_id, canDeleteMandate && deleteMandate && !!rowToEnd.hasMandate);

      if (isMounted()) {
        toast.success(t('invoice:autogiro.endAutogiroSuccess') as string);
        setRowToEnd(undefined);
        // Ny dateStamp läser om både listan och siffran på fliken.
        setDateStamp(new Date());
      }
    } catch (e) {
      // Interceptorn visar serverns meddelande.
      ConsoleHelper.log(e);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'memberNr',
      headerName: t('invoice:autogiro.tableHeader.memberNr'),
      sortable: true,
      flex: 0.7,
      renderCell: (params: GridCellParams) => {
        return <Link to={`${RoutesEnum.MemberDetails}/${params.row.customer_id}`}>{params.row.memberNr}</Link>;
      },
    },
    {
      field: 'firstName',
      headerName: t('invoice:autogiro.tableHeader.firstName'),
      sortable: true,
      flex: 0.8,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.firstName}</>;
      },
    },
    {
      field: 'lastName',
      headerName: t('invoice:autogiro.tableHeader.lastName'),
      sortable: true,
      flex: 1.1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.lastName}</>;
      },
    },
    {
      field: 'periodicity',
      headerName: t('invoice:autogiro.tableHeader.periodicity'),
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      type: 'number',
      flex: 0.8,
      renderCell: (params: GridCellParams) => {
        return <>{displayAutogiroStatus(params.row, t)}</>;
      },
    },
    {
      field: 'created',
      headerName: t('invoice:autogiro.tableHeader.createdAt'),
      sortable: true,
      flex: 0.8,
      renderCell: (params: GridCellParams) => {
        return <>{DateHelper.getDateTime(params.row.created)}</>;
      },
    },
    {
      field: 'nextInvoiceAfter',
      headerName: t('invoice:autogiro.tableHeader.nextInvoiceAfter'),
      sortable: true,
      flex: 0.8,
      renderCell: (params: GridCellParams) => {
        const warnings = renderWarnings(params.row);
        // Datumet stryks över bara när dragningen faktiskt inte blir av. Ett medgivande som
        // är på väg, alltså nytt eller väntande, hinner i de flesta fall bli godkänt innan dess.
        const isStrikethrough =
          mandateBlocksPayment(params.row.hasMandate, params.row.mandateStatus) || isExpireDateAhead(params.row) || isMissingAdmDate(params.row);
        return (
          <Box display='flex' alignItems='center' gap={0.5}>
            <span style={isStrikethrough ? { textDecoration: 'line-through' } : undefined}>{DateHelper.getDate(params.row.nextInvoiceAfter)}</span>
            {warnings}
          </Box>
        );
      },
    },
    {
      field: 'nextInvoiceAmount',
      headerAlign: 'left',
      align: 'left',
      headerName: t('invoice:autogiro.tableHeader.amount'),
      sortable: true,
      type: 'number',
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        if (params.row.nextInvoiceAmount === -1) return <>{t('invoice:autogiro.amountNotDetermined')}</>;
        return <AmountWithCurrency amount={params.row.nextInvoiceAmount} />;
      },
    },
    {
      // Knappen visas bara på de rader som kräver en åtgärd, övriga får en tom cell så att
      // listan ser ut som förut för den som inget behöver göra.
      field: 'actions',
      headerName: '',
      sortable: false,
      flex: 0.8,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params: GridCellParams) => {
        if (!needsAction(params.row)) return null;

        return (
          <Button variant='outlined' color='error' size='small' startIcon={<LinkOff fontSize='small' />} onClick={() => setRowToEnd(params.row)}>
            {t('invoice:autogiro.endAutogiro')}
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <Box
        sx={{
          position: 'relative',
        }}
        ref={scrollRef}
      >
        <DataGrid<MemberAutogiroStatusResponse>
          disablePointer
          checkboxSelection={false}
          disableSelectionOnClick
          columns={columns}
          rows={rows}
          autoHeight
          dataModel={data}
          columnVisibilityModel={{
            autogiroMandate_id: false,
          }}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          sortModel={[{ field: searchFilter.sortCol, sort: searchFilter.sortDir }]}
          onSortModelChange={(newSortModel) => {
            if (newSortModel.length > 0) {
              searchFilterCallback({
                ...searchFilter,
                pageIndex: 0,
                sortCol: newSortModel[0].field,
                sortDir: newSortModel[0].sort,
              });
            } else {
              searchFilterCallback({
                ...searchFilter,
                pageIndex: 0,
                sortCol: searchFilter.sortCol,
                sortDir: searchFilter.sortDir === 'asc' ? 'desc' : 'asc',
              });
            }
          }}
          // onRowClick={onRowClick}
          selectionModel={[]}
          onSelectionModelChange={() => ConsoleHelper.log('')}
        />
      </Box>

      <DeleteConfirmationDialog
        open={!!rowToEnd}
        value=''
        title={t('invoice:autogiro.endAutogiroTitle')}
        modaltext={t('invoice:autogiro.endAutogiroConfirm', {
          name: `${rowToEnd?.firstName ?? ''} ${rowToEnd?.lastName ?? ''}`.trim(),
          memberNr: rowToEnd?.memberNr ?? '',
        })}
        confirmButtonColor='error'
        confirmButtonText={t('invoice:autogiro.endAutogiro')}
        extraContent={
          canDeleteMandate && rowToEnd?.hasMandate ? (
            <FormControlLabel
              sx={{ mt: 1 }}
              control={<Checkbox checked={deleteMandate} onChange={(e) => setDeleteMandate(e.target.checked)} />}
              label={t('invoice:autogiro.endAutogiroDeleteMandate')}
            />
          ) : undefined
        }
        onClose={(value) => {
          if (value) endAutogiro();
          else setRowToEnd(undefined);
        }}
      />
    </>
  );
};
